import { isWorkedType, type EntryType } from "./constants.js";
import { addDays, isWeekend, ymd } from "./dates.js";

export type EntryInput = {
  date: string; // ymd
  type: EntryType;
  hours: number;
  comment: string;
};

/**
 * What a weekday starts at before anyone types.
 *
 * A suggestion, never a filing: the number is seeded into the boxes on screen and
 * is only true of a week once that week is actually saved. Nothing derives "did
 * they fill it in" from this.
 */
export const DEFAULT_WEEKDAY_HOURS = 8;

/** A fresh week template: Mon–Fri worked 8h, weekend non-working. */
export function defaultEntries(monday: Date): EntryInput[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(monday, i);
    const weekend = isWeekend(date);
    return {
      date: ymd(date),
      type: weekend ? "NON_WORKING" : "WORKED",
      hours: weekend ? 0 : DEFAULT_WEEKDAY_HOURS,
      comment: "",
    };
  });
}

/** Only WORKED days contribute paid hours. */
export function computeTotal(entries: { type: EntryType; hours: number }[]): number {
  return entries.reduce((sum, e) => sum + (isWorkedType(e.type) ? e.hours : 0), 0);
}

/**
 * A worked day with no hours on it is not a worked day (TIME-6).
 *
 * The editor now opens a weekday already pointed at a project, so "I didn't work
 * Wednesday" is expressed by clearing the hours rather than by never answering.
 * Left alone, that would store a WORKED entry of 0 hours against a project — a
 * claim on that engagement for a day nobody worked, which reads as attributable
 * time in every count that groups by project and as nothing at all in every count
 * that sums hours. Neither is what the contractor said.
 *
 * Coercion rather than rejection, deliberately: this fires on every part-filled
 * week, and an error there would be a wall in front of ordinary progress rather
 * than a rule anyone needed explaining.
 *
 * The comment goes with the hours. A note explains work, and there is no longer
 * any work on this day for it to explain — keeping it would leave "client
 * go-live, stayed late" attached to a day off.
 *
 * Applied on both sides of the wire: the client runs it over what it is about to
 * send so the screen agrees with what gets stored, and `saveTimesheet` runs it
 * over `input.entries` before validation, because a rule the client can skip is
 * not a rule.
 */
export function settleEmptyWorkedDays(entries: EntryInput[]): EntryInput[] {
  return entries.map((entry) =>
    isWorkedType(entry.type) && entry.hours === 0
      ? { ...entry, type: "NON_WORKING", hours: 0, comment: "" }
      : entry
  );
}

/**
 * Bring a week filed under the old half-hour rule up to whole hours.
 *
 * `HOURS_STEP` was 0.5 and is now 1, and the database's CHECK was deliberately
 * left at the older, wider rule — so weeks holding 7.5 are still perfectly
 * storable and still load fine. What they can no longer do is *save*: the value
 * that came down the wire would fail `validateWeekEntries` on the way back up,
 * stranding a draft its owner never edited.
 *
 * So the rounding happens where the contractor can see it and refuse it: on load,
 * in the editor, on editable rows only, surfaced as a notice naming the days and
 * left unsaved until they press Save. An approved or locked week keeps its stored
 * values untouched — rounding one would be rewriting a record that was correct
 * when it was filed.
 *
 * `Math.round` takes 7.5 to 8: a half hour worked is rounded up, in the
 * contractor's favour, which is the only direction that cannot quietly shave paid
 * time off someone's week.
 *
 * Returns the days it changed so the caller can name them. Empty means the week
 * was already whole, which is every week filed since the rule changed.
 */
export function roundToWholeHours(entries: EntryInput[]): {
  entries: EntryInput[];
  changed: string[];
} {
  const changed: string[] = [];
  const rounded = entries.map((entry) => {
    const whole = Math.round(entry.hours);
    if (whole === entry.hours) return entry;
    changed.push(entry.date);
    return { ...entry, hours: whole };
  });
  return { entries: changed.length > 0 ? rounded : entries, changed };
}
